import {
    DragonBonesResourceItem,
    GameRandom,
    GameStorageManager,
    Inject,
    ISimpleControllerConfiguration,
    ResourceItem,
    SimpleBaseController,
    SoundManager,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AppStateEnum, GameModel } from './Models/Game/GameModel';
import { GameLogic } from './Logic/GameLogic';
import { MenuScreenController } from './Controllers/Menu/MenuScreenController';
import { GameScreenController } from './Controllers/Game/GameScreenController';
import { OrientationScreenController } from './Controllers/Common/OrientationScreenController';
import { SaveScreenController } from './Controllers/Menu/Modals/Save/SaveScreenController';
import { searchParam } from './Common/Utils';
import { FirebaseAPI } from '../FirebaseAPI';
import { FullscreenController } from './Controllers/Common/FullscreenController';

import isMobile from 'is-mobile';
import { SoundConstants } from './Constants/SoundConstants';

export interface IRootControllerOptions {
    version: string;
}

export class RootController extends SimpleBaseController {
    $model: GameModel;

    private _videoElement: HTMLVideoElement = null;
    private _videoSkipButtonElement: HTMLDivElement = null;

    public constructor(
        @Inject private _options: IRootControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _random: GameRandom,
        @Inject private _gameStorage: GameStorageManager,
        @Inject private _firebaseAPI: FirebaseAPI,
        @Inject private _soundManager: SoundManager
    ) {
        super('RootController');
    }

    protected getConfiguration(): ISimpleControllerConfiguration {
        return <ISimpleControllerConfiguration>{
            sounds: [
                {
                    id: SoundConstants.GAME,
                    type: SoundType.Music,
                },
            ],
            resources: {
                manAnimation: new DragonBonesResourceItem(
                    'manAnimation',
                    '/images/dragonBones/man_char/man_char'
                ),
                girlAnimation: new DragonBonesResourceItem(
                    'girlAnimation',
                    '/images/dragonBones/girl_char/girl_char'
                ),
                bonusFxAnimation: new DragonBonesResourceItem(
                    'bonusFxAnimation',
                    '/images/dragonBones/bonus_fx/bonus_fx'
                ),
            },
            childControllers: {
                OrientationScreen: {
                    view: OrientationScreenController,
                },
                Fullscreen: {
                    view: FullscreenController,
                    model: this.$model,
                },
            },
            modelWatch: {
                appState: async (state: AppStateEnum) => {
                    const screen = this.$childControllers.get('screen');
                    if (screen) {
                        await this.removeChildControllerByNameAsync('screen');
                    }

                    switch (state) {
                        case AppStateEnum.menu:
                            this.addChildController(
                                {
                                    view: MenuScreenController,
                                    model: this.$model,
                                },
                                'screen',
                                0
                            );
                            break;

                        case AppStateEnum.save:
                            this.addChildController(
                                {
                                    view: SaveScreenController,
                                    model: this.$model,
                                },
                                'screen',
                                0
                            );
                            break;

                        case AppStateEnum.game:
                            await this.playVideo();
                            await this.addChildControllerAsync(
                                {
                                    view: GameScreenController,
                                    model: this.$model,
                                },
                                true,
                                'screen',
                                0
                            );
                            break;
                    }
                },
            },
        };
    }

    protected async onInitialize(): Promise<void> {
        await this._gameLogic.initStorage();
        await this._gameLogic.initDB();
        await this._gameLogic.loadDB();

        const skipMenuValue = searchParam('skipMenu');
        const skipMenu = skipMenuValue === '1' || skipMenuValue === 'true';

        if (skipMenu) {
            this._gameLogic.showGameScreen();
        } else {
            this._gameLogic.showMenuScreen();
        }
    }

    private async playVideo(): Promise<void> {
        return new Promise(resolve => {
            if (this._soundManager.isSoundPlaying(SoundConstants.MENU)) {
                this._soundManager.stop(SoundConstants.MENU);
            }

            const skipVideoParam = searchParam('skipVideo');
            const skipVideo =
                skipVideoParam === '1' || skipVideoParam === 'true';

            this._videoElement = document.getElementById(
                'video-frame'
            ) as HTMLVideoElement;

            if (skipVideo || !this._videoElement) {
                this.stopVideo();
                resolve();
            } else {
                this._videoSkipButtonElement = document.getElementById(
                    'video-skip-button'
                ) as HTMLDivElement;

                this._videoSkipButtonElement.style.visibility = 'visible';
                this._videoElement.style.visibility = 'visible';

                this._videoSkipButtonElement.onclick = () => {
                    this.stopVideo();
                    resolve();
                };

                const videoWoman = 'If0mEZ3Lx94';
                const videoMan = 'yrXkXb39hWI';

                const params =
                    'wmode=transparent&controls=0&autoplay=1&showinfo=0&enablejsapi=1&rel=0&&playsinline=1&playlist=';

                this._videoElement.removeAttribute('allowfullscreen');
                this._videoElement.setAttribute('donotallowfullscreen', '');

                this._videoElement.src = this.$model.isWoman
                    ? `https://www.youtube.com/embed/${videoWoman}?${params}${videoWoman}`
                    : `https://www.youtube.com/embed/${videoMan}?${params}${videoMan}`;
            }
        });
    }

    private stopVideo(): void {
        if (this._videoElement && this._videoSkipButtonElement) {
            document.body.removeChild(this._videoElement);
            document.body.removeChild(this._videoSkipButtonElement);

            this.$model.isVideoPlayed = true;

            this._videoElement = null;
            this._videoSkipButtonElement = null;
        }
    }
}
